import React, { useEffect, useCallback } from 'react'
import { HStack, Text, Icon, Button, FormControl, FormErrorMessage, FormLabel, Box } from '@chakra-ui/react'
import { FormikErrors, FormikTouched } from 'formik'
import {
  LabeledInput,
  Input,
  AutoSuggest,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  theme
} from '@apex/component-library'
import { FaQuestionCircle } from 'react-icons/fa'

interface FormikValues {
  intermediaryName?: string
  intermediaryAccountNumber?: string
  intermediaryAddress?: string
  intermediaryAddressLine2?: string
  intermediaryCity?: string
  intermediaryState?: string
  intermediaryZip?: string
  intermediaryCountry?: string
}

interface IntermediaryFormProps {
  formikValues: FormikValues
  formikBlur: (e: React.FocusEvent<any>) => void
  formikTouched: FormikTouched<FormikValues>
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void
  setTouched: (fields: object, validate: boolean) => void
  errors: FormikErrors<FormikValues>
  countryList: { value: string }[]
  countryListLoading: boolean
  countryListFailedMessage?: string
}

const WireIntermediaryAccount = ({
  formikValues,
  setFieldValue,
  formikTouched,
  formikBlur,
  errors,
  setTouched,
  countryList,
  countryListLoading,
  countryListFailedMessage
}: IntermediaryFormProps) => {
  const {
    intermediaryName,
    intermediaryAccountNumber,
    intermediaryAddress,
    intermediaryAddressLine2,
    intermediaryCity,
    intermediaryState,
    intermediaryZip,
    intermediaryCountry
  } = formikValues

  const clearIntermediaryForm = () => {
    setFieldValue('intermediaryName', '')
    setFieldValue('intermediaryAccountNumber', '')
    setFieldValue('intermediaryAddress', '')
    setFieldValue('intermediaryAddressLine2', '')
    setFieldValue('intermediaryCity', '')
    setFieldValue('intermediaryState', '')
    setFieldValue('intermediaryZip', '')
    setFieldValue('intermediaryCountry', '')
    setFieldValue('intermediaryCountryCode', '')
    setTouched({}, true)
  }

  const clearIntermediaryFormMemo = useCallback(clearIntermediaryForm, [])

  useEffect(clearIntermediaryFormMemo, [clearIntermediaryFormMemo])

  return (
    <>
      <Text as='h3' textStyle='h3'>
        Intermediary Account
      </Text>
      <HStack spacing={8}>
        <LabeledInput
          label='Name'
          aria-label='intermediary name'
          name='intermediaryName'
          isRequired
          onBlur={formikBlur}
          value={intermediaryName}
          onChange={(e) => {
            setFieldValue('intermediaryName', e.target.value)
          }}
          errorMessage={errors?.intermediaryName}
          isInvalid={formikTouched.intermediaryName && !!errors?.intermediaryName}
        />
        <LabeledInput
          label='Account # or Routing #'
          aria-label='intermediary account number'
          name='intermediaryAccountNumber'
          isRequired
          onBlur={formikBlur}
          value={intermediaryAccountNumber}
          onChange={(e) => {
            setFieldValue('intermediaryAccountNumber', e.target.value)
          }}
          errorMessage={errors?.intermediaryAccountNumber}
          isInvalid={formikTouched.intermediaryAccountNumber && !!errors?.intermediaryAccountNumber}
        />
      </HStack>
      <LabeledInput
        label='Address'
        aria-label='intermediary address'
        name='intermediaryAddress'
        isRequired
        onBlur={formikBlur}
        value={intermediaryAddress}
        onChange={(e) => {
          setFieldValue('intermediaryAddress', e.target.value)
        }}
        errorMessage={errors?.intermediaryAddress}
        isInvalid={formikTouched.intermediaryAddress && !!errors?.intermediaryAddress}
      />
      <LabeledInput
        label='Address Line 2'
        name='intermediaryAddressLine2'
        onBlur={formikBlur}
        value={intermediaryAddressLine2}
        onChange={(e) => {
          setFieldValue('intermediaryAddressLine2', e.target.value)
        }}
        errorMessage={errors?.intermediaryAddressLine2}
        isInvalid={formikTouched.intermediaryAddressLine2 && !!errors?.intermediaryAddressLine2}
      />
      <HStack spacing={8}>
        <LabeledInput
          label='City'
          aria-label='intermediary city'
          name='intermediaryCity'
          isRequired
          onBlur={formikBlur}
          value={intermediaryCity}
          onChange={(e) => {
            setFieldValue('intermediaryCity', e.target.value)
          }}
          errorMessage={errors?.intermediaryCity}
          isInvalid={formikTouched.intermediaryCity && !!errors?.intermediaryCity}
        />
        <FormControl isInvalid={!!errors?.intermediaryState}>
          <FormLabel color={!!errors?.intermediaryState ? theme.colors.red[500] : 'black'}>
            State / Province{' '}
            <Popover
              placement='top-start'
              modifiers={[
                {
                  name: 'offset',
                  options: {
                    offset: [-5, 10]
                  }
                }
              ]}
              trigger='click'
              defaultIsOpen={false}
            >
              <PopoverTrigger>
                <Button
                  aria-label='intermediary state popover'
                  variant='link'
                  h='1rem'
                  d='inline-block'
                  minW='1rem'
                  lineHeight={0}
                  border={0}
                  rounded='full'
                >
                  <Icon as={FaQuestionCircle} color='black'>
                    Trigger Popover
                  </Icon>
                </Button>
              </PopoverTrigger>
              <PopoverContent color='black'>
                <PopoverArrow />
                <PopoverBody>
                  For Domestic addresses, state identifiers must use United States Postal Service state abbreviations.
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </FormLabel>
          <Input
            aria-label='intermediary state'
            name='intermediaryState'
            isRequired
            onBlur={formikBlur}
            value={intermediaryState}
            onChange={(e) => {
              setFieldValue('intermediaryState', e.target.value)
            }}
            isInvalid={formikTouched.intermediaryState && !!errors?.intermediaryState}
          />
          <FormErrorMessage>{errors?.intermediaryState}</FormErrorMessage>
        </FormControl>
      </HStack>
      <HStack spacing={8}>
        <LabeledInput
          label='ZIP / Postal Code'
          aria-label='intermediary zip'
          name='intermediaryZip'
          value={intermediaryZip}
          onBlur={formikBlur}
          onChange={(e) => setFieldValue('intermediaryZip', e.target.value)}
          errorMessage={errors?.intermediaryZip}
          isInvalid={formikTouched.intermediaryZip && !!errors?.intermediaryZip}
        />
        <Box width='100%'>
          <AutoSuggest
            label='Country'
            aria-label='intermediary country'
            name='intermediaryCountry'
            inputValue={intermediaryCountry || ''}
            onBlur={formikBlur}
            onChange={(e) => {
              setFieldValue('intermediaryCountry', e.target.value)
            }}
            dataSet={countryList}
            onSelectItem={(value) => setFieldValue('intermediaryCountry', value)}
            isDisabled={countryListLoading || !!countryListFailedMessage}
            placeholder={countryListLoading ? 'Loading...' : ''}
            errorMessage={countryListFailedMessage || errors?.intermediaryCountry}
            isInvalid={
              !!countryListFailedMessage || (formikTouched.intermediaryCountry && !!errors?.intermediaryCountry)
            }
            isRequired
          />
        </Box>
      </HStack>
    </>
  )
}

export default WireIntermediaryAccount
